/**
 * Measured slack between an element and its neighbours (omega-parallax.js gapSide, L166–222;
 * docs/behavior/hero-and-motion.md §6.4). Untransformed geometry only (offsetTop / offsetHeight),
 * compared only between boxes that share an offsetParent.
 */
import { REVEAL_SEL, ROOT_GAP } from './constants';

/** What is read from a sibling; non-HTML children (SVG) have none of it and count as empty. */
interface BoxLike {
  readonly offsetParent?: unknown;
  readonly offsetTop?: number;
  readonly offsetHeight?: number;
}

/** The subset of an element the measurement reads; HTMLElement satisfies it. */
export interface LayoutNode {
  readonly parentElement: LayoutNode | null;
  readonly offsetParent: unknown;
  readonly offsetTop: number;
  readonly offsetHeight: number;
  readonly clientHeight: number;
  readonly children: ArrayLike<object>;
}

function layoutH(el: BoxLike): number {
  return el.offsetHeight || 0;
}

/** Gap above (`dir` −1) or below (+1), ascending at most four ancestors. */
export function gapSide(el: LayoutNode, dir: -1 | 1, clips: (node: LayoutNode) => boolean): number {
  let node: LayoutNode | null = el;
  let total = 0;
  let guard = 0;
  while (node !== null && guard++ < 4) {
    const p: LayoutNode | null = node.parentElement;
    if (p === null) return ROOT_GAP;
    const nt = node.offsetTop;
    const nb = nt + layoutH(node);
    let best = Infinity;
    for (let i = 0; i < p.children.length; i++) {
      const s2 = p.children[i] as BoxLike | undefined;
      if (s2 === undefined || s2 === node || layoutH(s2) < 1) continue;
      if (s2.offsetParent !== node.offsetParent) continue;
      const st2 = s2.offsetTop ?? 0;
      const sb2 = st2 + layoutH(s2);
      if (dir < 0 && sb2 <= nt + 0.5) best = Math.min(best, nt - sb2);
      else if (dir > 0 && st2 >= nb - 0.5) best = Math.min(best, st2 - nb);
    }
    if (Number.isFinite(best)) return Math.max(0, total + best);

    let inset: number;
    if (node.offsetParent === p) {
      inset = dir < 0 ? nt : p.clientHeight - nb;
    } else if (node.offsetParent === p.offsetParent) {
      inset = dir < 0 ? nt - p.offsetTop : p.offsetTop + layoutH(p) - nb;
    } else {
      return Math.max(0, total);
    }
    if (!Number.isFinite(inset) || inset < 0) inset = 0;
    total += inset;
    if (clips(p)) return Math.max(0, total);
    node = p;
  }
  return Math.max(0, total);
}

export function neighbourGap(el: LayoutNode, clips: (node: LayoutNode) => boolean): number {
  return Math.min(gapSide(el, -1, clips), gapSide(el, 1, clips));
}

/** True when the element or one of its siblings is animated on reveal. */
export function revealNear(el: Element): boolean {
  if (el.matches(REVEAL_SEL)) return true;
  const p = el.parentElement;
  if (p === null) return false;
  for (const k of Array.from(p.children)) {
    if (k !== el && k.matches(REVEAL_SEL)) return true;
  }
  return false;
}
